<script>
import ActionTemplates from './ActionTemplates'
import NestedObject from './NestedObject'
import ModalCard from './ModalCard'
import ActionStatus from './ActionStatus'
import ActionProgress from './ActionProgress'
import WorkflowExecution from './WorkflowExecution'
import TraitSelection from './TraitSelection'
import SelectPreviousWorkflow from './SelectPreviousWorkflow'

import {find, filter} from 'lodash'

window.WorkflowExecution = WorkflowExecution

export default {

  components: {
    ModalCard,
    ActionStatus,
    ActionProgress,
    TraitSelection,
    SelectPreviousWorkflow
  },

  props: {
    workflowRunner: {
      type: Object
    },
  },

  data() {
    return {
      isLoading: false,
      isFinished: false,
      finishedWithErrors: false,
      startedActions: {},

      showModal: true,

      runtimeOptions: {
        traits: [],
        selectedPreviousWorkflows: {}
      },

      scrollTo: null,

      previousWorkflows: WorkflowExecution.storage.all()
    }
  },

  computed: {
    actionTemplates() {
      return ActionTemplates.storage.allGrouped()
    },

    workflow() {
      return this.workflowRunner.workflow
    },

    isPending() {
      return !this.isLoading && !this.isFinished && !this.finishedWithErrors
    }
  },

  watch: {
    showModal() {
      if (!this.showModal) {
        this.$emit('close')
      }
    },
    finishedWithErrors() {
      if (this.finishedWithErrors) {
        this.isLoading = false
      }
    }
  },

  methods: {
    runAgain() {
      this.startedActions = {}
      this.isLoading = false
      this.isFinished = false
      this.finishedWithErrors = false
      this.workflowRunner.reset()
    },

    run() {
      this.startedActions = {}
      this.workflowRunner.run(this.runtimeOptions)
    },

    processOutput(action, output) {
      let beautyfiedOutput = {}
      for (let [key, value] of Object.entries(this.actionTemplates[action.template_id].output)) {
        beautyfiedOutput[key] = NestedObject.find(output, value)
      }

      return beautyfiedOutput
    }
  },

  created() {

    this.workflowRunner.bus.$on('start', ()=>{
      this.finishedWithErrors = false
      this.isLoading = true
      this.isFinished = false
    })

    this.workflowRunner.bus.$on('complete', (results)=>{

      this.$nextTick(()=>{
        WorkflowExecution.create(this.workflowRunner)
      })

      this.isLoading = false
      this.isFinished = true
    })

    this.workflowRunner.bus.$on('start.action', (action)=>{
      this.$set(this.startedActions, action.uuid, {
        uuid: action.uuid,
        name: action.name,
        isLoading: true,
        result: null,
        error: null
      })
    })

    this.workflowRunner.bus.$on('validation-error.action', (actionWithError, error) => {
      this.finishedWithErrors = true
      let action = this.startedActions[actionWithError.uuid]
      this.$set(action, 'error', error)
      this.$set(action, 'isLoading', false)
    })
    this.workflowRunner.bus.$on('network-error.action', (actionWithError, error) => {
      this.finishedWithErrors = true
      let action = this.startedActions[actionWithError.uuid]
      this.$set(action, 'error', `${error.name}: ${error.message}`)
      this.$set(action, 'isLoading', false)
    })

    this.workflowRunner.bus.$on('complete.action', (completedAction, output)=>{
      this.scrollTo = `.action-${completedAction.uuid}`

      let action = this.startedActions[completedAction.uuid]
      this.$set(action, 'result', this.processOutput(completedAction, output))
      this.$set(action, 'isLoading', false)
    })
  }

}
</script>

<template>
  <b-modal :active.sync="showModal" scroll="keep">
    <modal-card :title="`Run Workflow (${workflow.name})`" :scrollTo="scrollTo">
      <template slot="body">
        <p class="has-text-grey" v-if="finishedWithErrors">Your workflow has finished with errors.</p>
        <p class="has-text-grey" v-if="isFinished">Your workflow has finished successfully!</p>
        <p class="has-text-grey" v-if="isLoading">Your workflow is now being executed!</p>
        <br>

        <div v-for="action in workflow.actions" :key="action.uuid" :class="`action-${action.uuid}`">

          <div class="columns">
            <div class="column is-3">
              <label class="label">{{ action.name }}</label>
              <action-status :action="startedActions[action.uuid]"></action-status>
            </div>
            <div class="column is-9">
              <div v-if="action.template().method == 'SelectWorkflow'">
                <select-previous-workflow v-model="runtimeOptions.selectedPreviousWorkflows[action.uuid]" :action="action"></select-previous-workflow>
              </div>

              <action-progress :action="action" :startedAction="startedActions[action.uuid]"></action-progress>

              <trait-selection
                v-if="action.runtime_traits && isPending"
                v-model="runtimeOptions.traits"
                :traits="action.template().traits"
                :disabled="isLoading"
              ></trait-selection>

            </div>
          </div>

          <hr>

        </div>
      </template>
      <template slot="footer">
        <div>
          <div class="buttons">
            <b-button @click="$emit('close')">Close</b-button>
          </div>
        </div>

        <div>
          <b-button :disabled="isLoading" @click="runAgain" v-if="isFinished || finishedWithErrors" type="is-primary" icon-left="reload">Run Again</b-button>
          <b-button :disabled="isLoading" @click="run" v-else type="is-primary" icon-left="cloud-upload-outline">Run Workflow</b-button>
        </div>

      </template>
    </modal-card>
  </b-modal>
</template>

<style lang="scss">
.is-relative {
  position: relative;
}
</style>